import { useCallback, useEffect, useRef, useState } from "react";
import { errorLog } from "../utils/errorLog";
import { classifyStorageError, emitStorageToast } from "../utils/storageError";

/** Choice of Web Storage backing for {@link usePersistedState}. */
export type StorageKind = "local" | "session";

/** Options for {@link usePersistedState}. */
export interface PersistedStateOptions<T> {
  /** Which Web Storage backend to use; defaults to "local". */
  storage?: StorageKind;
  /** Custom decoder for the persisted string; defaults to JSON.parse. */
  parse?: (raw: string) => T;
  /** Custom encoder for the persisted string; defaults to JSON.stringify. */
  stringify?: (value: T) => string;
}

/**
 * Same-tab broadcast registry: every mounted `usePersistedState` instance of
 * the same storage key subscribes here, and a write from any one of them is
 * pushed to all the others. Without this, each hook call is an isolated
 * `useState` copy — a Settings toggle flipped in one component never reached a
 * consumer that was already mounted elsewhere (cross-tab `storage` events do
 * not fire in the tab that wrote).
 */
type KeyListener = (next: unknown) => void;
const keyListeners = new Map<string, Set<KeyListener>>();

/*
 * Accepted edge: a consumer that mounts in the same React commit in which a
 * sibling instance writes can read pre-write storage in its `useState`
 * initializer and subscribe just after the sibling's effect notified, holding
 * the stale value until the next write. No current consumer mounts and writes
 * the same key in one commit; a module-level last-broadcast cache would close
 * the window but outlives externally-reset storage, which is worse.
 */

/** One broadcast channel per (storage backend, key) pair. */
function channelOf(kind: StorageKind, key: string): string {
  return `${kind}:${key}`;
}

function subscribeKey(channel: string, fn: KeyListener): () => void {
  let set = keyListeners.get(channel);
  if (!set) {
    set = new Set();
    keyListeners.set(channel, set);
  }
  set.add(fn);
  return () => {
    set.delete(fn);
    if (set.size === 0) keyListeners.delete(channel);
  };
}

function notifyKey(channel: string, next: unknown, source: KeyListener): void {
  const set = keyListeners.get(channel);
  if (!set) return;
  for (const fn of set) {
    if (fn !== source) fn(next);
  }
}

/** Resolves the Storage instance for a {@link StorageKind}, SSR-safe. */
function getStorage(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    // Expected: storage access can throw in private-mode Safari and
    // sandboxed iframes. Caller treats null as "no persistence".
    return null;
  }
}

/** Reads and decodes a persisted value, returning `fallback` on miss/parse error.
 *
 * Parse errors are not user-facing — the decoded shape may have changed,
 * the value may be from an older schema, or another tab may have written
 * something the new code can't read. Silently falling back to the default
 * is the right behaviour, but we still route the error through `errorLog`
 * so dev builds surface "your localStorage entry for X is corrupt" while
 * production stays silent. */
export function readPersisted<T>(
  key: string,
  fallback: T,
  options: PersistedStateOptions<T> = {},
): T {
  const storage = getStorage(options.storage ?? "local");
  if (!storage) return fallback;
  const parse = options.parse ?? ((raw) => JSON.parse(raw) as T);
  try {
    const raw = storage.getItem(key);
    if (raw == null) return fallback;
    return parse(raw);
  } catch (err) {
    errorLog("persistedState.read", err, { key });
    return fallback;
  }
}

/**
 * Encodes and writes a value to the chosen storage. Classifies failures:
 *
 * - QuotaExceededError → emits a debounced toast ("Local storage is full —
 * settings saved in this session only") via the storage- toast bridge. The
 * user's preference no longer silently reverts on reload without explanation.
 * - SecurityError / private mode → emits the access-denied toast instead
 * (one-time per session via the debounce budget). - Other DOMExceptions →
 * logged via `errorLog` but not surfaced; these are environment errors the
 * user can't act on.
 *
 * Returns `true` on success so call sites that need to know (e.g.
 * `usePersistedState` snapping React state back) can branch.
 */
export function writePersisted<T>(
  key: string,
  value: T,
  options: PersistedStateOptions<T> = {},
): boolean {
  const storageKind = options.storage ?? "local";
  const storage = getStorage(storageKind);
  if (!storage) return false;
  const stringify = options.stringify ?? ((v: T) => JSON.stringify(v));
  try {
    storage.setItem(key, stringify(value));
    return true;
  } catch (err) {
    const classified = classifyStorageError(err);
    errorLog("persistedState.write", err, { key, kind: classified.kind });
    if (classified.kind === "quota-exceeded") {
      emitStorageToast("quota-exceeded", key);
    } else if (classified.kind === "access-denied") {
      emitStorageToast("access-denied", key);
    }
    return false;
  }
}

/** React state mirrored to localStorage or sessionStorage with optional codec.
 *
 * All mounted instances of the same key stay in sync within the tab: a write
 * from any instance is broadcast to every other via the module-level
 * subscriber set above, so (e.g.) a verification-mode flip in Settings reaches
 * an already-mounted `useInspect` and re-runs its analysis effect. */
export function usePersistedState<T>(
  key: string,
  initialValue: T,
  options: PersistedStateOptions<T> = {},
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKind: StorageKind = options.storage ?? "local";
  const parse = options.parse;

  const [value, setValue] = useState<T>(() =>
    readPersisted(key, initialValue, { storage: storageKind, parse }),
  );

  // Keep the stringify codec in a ref so the write effect always reads the
  // latest closure without listing it as a dependency. Callers commonly pass
  // an inline `stringify`, whose identity churns every render; depending on it
  // would re-run the effect — and fire a redundant synchronous localStorage
  // write — on every render, not just when key/value/storageKind change.
  const stringifyRef = useRef(options.stringify);
  stringifyRef.current = options.stringify;

  // The last value this instance broadcast or received. Gates the write
  // effect's notify so a fresh mount (whose read produces a new-but-equal
  // object) doesn't churn its siblings, and an incoming broadcast doesn't
  // echo back to its origin.
  const lastSyncedRef = useRef<T>(value);

  // True while the current `value` arrived from a sibling's broadcast; the
  // write effect then skips the redundant storage write (the origin already
  // persisted it) instead of N instances re-writing the same bytes.
  const receivedRef = useRef(false);

  // Receive a value another instance of the same key just wrote.
  const applyExternal = useCallback((next: unknown) => {
    lastSyncedRef.current = next as T;
    receivedRef.current = true;
    setValue(next as T);
  }, []);

  useEffect(
    () => subscribeKey(channelOf(storageKind, key), applyExternal),
    [key, storageKind, applyExternal],
  );

  useEffect(() => {
    const external = receivedRef.current && Object.is(lastSyncedRef.current, value);
    receivedRef.current = false;
    if (external) return; // origin instance already wrote these bytes
    writePersisted(key, value, { storage: storageKind, stringify: stringifyRef.current });
    if (!Object.is(lastSyncedRef.current, value)) {
      lastSyncedRef.current = value;
      notifyKey(channelOf(storageKind, key), value, applyExternal);
    }
  }, [key, value, storageKind, applyExternal]);

  const update = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) => (typeof next === "function" ? (next as (p: T) => T)(prev) : next));
  }, []);

  return [value, update];
}
